/**
 * Asks before something that cannot be taken back, or that a person should mean. It is a `Dialog`
 * with a question, an outcome-named confirm button and a Cancel.
 *
 * Name the button for what happens, never "OK" or "Yes": "Remove patient", "Delete my data". A
 * button that ends or deletes is `tone="danger"`, and then Cancel takes focus when the dialog
 * opens so Enter cannot confirm by accident.
 *
 * `onConfirm` returns the problem to show, or `null` when it worked. A problem keeps the dialog
 * open with the words inside it and the button ready to press again. On success the dialog calls
 * `onClose`. While it waits, the button shows a spinner and Escape and Cancel do nothing, so the
 * action cannot be abandoned half-sent.
 *
 * `fallbackFocus` names where focus goes after a success when the control that opened the dialog is
 * gone, as when the row it sat in left the list. The browser returns focus to the opener on its
 * own, and with no opener left that is the top of the document, which says nothing to a screen
 * reader. The target takes focus only when nothing else has it.
 *
 * `fields` puts controls between the question and the reason box, for a choice the action needs.
 *
 * `reasonRequired` adds a reason box that must be filled in before the button works, and hands the
 * words to `onConfirm`. Staff actions use it in place of a permanently open form.
 */
import * as stylex from "@stylexjs/stylex";
import type { ReactNode } from "react";
import { useEffect, useId, useState } from "react";
import "@lattice/tokens/space.stylex";
import { space } from "@lattice/tokens/space.stylex";
import { Button } from "./Button.tsx";
import { Dialog } from "./Dialog.tsx";
import { Field } from "./Field.tsx";
import { Status } from "./Status.tsx";
import { Text } from "./Text.tsx";
import { Textarea } from "./Textarea.tsx";
import { visuallyHidden } from "./visuallyHidden.ts";
export interface ConfirmDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  /** The question, as a sentence: "Remove Ada Chen from this clinic?" */
  readonly title: string;
  /** What will happen, and what will not. */
  readonly children?: ReactNode;
  /** Controls that come before the reason, such as the place a record moves to. */
  readonly fields?: ReactNode;
  /** The outcome, as the button says it: "Remove patient". */
  readonly confirmLabel: string;
  /** What Cancel says. Name the outcome of not going ahead: "Keep account active". */
  readonly cancelLabel?: string;
  readonly tone?: "default" | "danger";
  /** Starts focus on Cancel without the red button, for an action that widens access to records. */
  readonly cancelFirst?: boolean;
  readonly reasonRequired?: boolean;
  readonly reasonLabel?: string;
  readonly onConfirm: (reason: string) => Promise<string | null>;
  /** Where focus goes after a success if the opener is gone: a heading or a list. */
  readonly fallbackFocus?: () => HTMLElement | null;
  readonly testId?: string;
}

/** How many frames, about half a second, a removed opener is waited for. */
const SETTLE_FRAMES = 30;

/**
 * After the dialog has closed and the browser has moved focus back, if there was a control to. The
 * opener can be removed a moment after the dialog closes, when the list re-renders under load, so
 * the check repeats for a few frames and only a document with nothing focused takes the fallback.
 */
function settleFocus(target: () => HTMLElement | null, frames = SETTLE_FRAMES): void {
  window.requestAnimationFrame(() => {
    if (document.activeElement === null || document.activeElement === document.body) {
      const element = target();
      if (element !== null && element.isConnected) {
        if (!element.hasAttribute("tabindex")) element.tabIndex = -1;
        element.focus();
        return;
      }
    }
    if (frames > 0) settleFocus(target, frames - 1);
  });
}
export function ConfirmDialog({
  open,
  onClose,
  children,
  fields,
  confirmLabel,
  cancelLabel = "Cancel",
  tone = "default",
  cancelFirst = false,
  reasonRequired = false,
  reasonLabel = "Reason",
  onConfirm,
  fallbackFocus,
  testId,
  title
}: ConfirmDialogProps) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  /* Set once the confirm button is pressed with the reason still empty, so the box says so. */
  const [pressedEarly, setPressedEarly] = useState(false);
  const reasonId = useId();

  /* Each opening starts with an empty reason box and no stale problem. */
  useEffect(() => {
    if (!open) return;
    setReason("");
    setProblem(null);
    setPressedEarly(false);
  }, [open]);
  const missing = reasonRequired && reason.trim() === "";
  const danger = tone === "danger";
  const confirm = async () => {
    setBusy(true);
    setProblem(null);
    try {
      const failure = await onConfirm(reason.trim());
      if (failure === null) {
        onClose();
        if (fallbackFocus !== undefined) settleFocus(fallbackFocus);
      } else setProblem(failure);
    } catch {
      setProblem("That did not go through. Try again.");
    } finally {
      setBusy(false);
    }
  };
  const close = () => {
    if (!busy) onClose();
  };
  return <Dialog open={open} onClose={close} title={title} size="sm" testId={testId} footer={<>
          <Button variant="secondary" onClick={close} disabled={busy} fullWidth="phone" initialFocus={danger || cancelFirst}>
            {cancelLabel}
          </Button>
          <Button variant={danger ? "danger" : "primary"} loading={busy} fullWidth="phone" disabled={missing} describedBy={missing ? `${reasonId}-hint` : undefined} onDisabledPress={() => {
      setPressedEarly(true);
      document.getElementById(reasonId)?.focus();
    }} onClick={() => void confirm()}>
            {confirmLabel}
          </Button>
        </>}>
      <div className="x78zum5 xdt5ytf x626il7">
        {children !== undefined && <Text>{children}</Text>}
        {fields}
        {reasonRequired && <Field label={reasonLabel} id={reasonId} hint={missing ? "Required. Add a reason first." : "Required."} error={missing && pressedEarly ? "Add a reason before you continue." : null}>
            {control => <Textarea {...control} value={reason} onChange={next => {
          setReason(next);
          setPressedEarly(false);
        }} rows={3} />}
          </Field>}
        {/* Always in the page, so the words put into it are announced while the action runs. */}
        <span role="status" {...stylex.props(visuallyHidden.hidden)}>
          {busy ? "Working on it" : ""}
        </span>
        {problem !== null && <div role="alert">
            <Status state="problem" live={false}>
              {problem}
            </Status>
          </div>}
      </div>
    </Dialog>;
}